import { useEffect, useMemo, useState } from 'react'
import { Navigate, useLocation, useNavigate } from 'react-router-dom'
import { CheckCircle2 } from 'lucide-react'
import { calculateNights, formatCurrency } from '@hotel/shared/utils'
import { getPrimaryImage } from '../api/client'
import { roomImageFallback } from '../components/roomImageFallbacks'
import {
  useAvailabilityQuery,
  useCreateReservation,
  useCustomerLogin,
  useCustomerRegister,
} from '../api/hooks'
import { executeRecaptcha } from '../lib/recaptcha'
import type { AvailabilityResult, BookingForm, Customer, SearchState } from '../api/types'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { BookingDateGuestPicker } from '@/components/BookingDateGuestPicker'
import { PhoneInput } from '@/components/PhoneInput'

const fieldLabel = 'mb-2 block text-sm font-medium text-wood-dark'

export function BookingPage({
  customer,
  setCustomerAuth,
}: {
  customer: Customer | null
  setCustomerAuth: (next: { token: string; customer: Customer } | null) => void
}) {
  const navigate = useNavigate()
  const location = useLocation() as {
    state?: { room?: AvailabilityResult; search?: SearchState }
  }
  const initialRoom = location.state?.room
  const initialSearch = location.state?.search
  const [search, setSearch] = useState<SearchState | undefined>(initialSearch)
  const liveAvailability = useAvailabilityQuery(search ?? null)
  // Merge the live API result with the snapshot captured during search.
  // The fresh response carries the up-to-date pricing (estimated_total,
  // extra_person, availability), but it can omit imagery/marketing fields
  // when those aren't configured on the backend — in that case we keep
  // the values from the initial state so the summary card stays populated.
  const room = useMemo<AvailabilityResult | undefined>(() => {
    if (!initialRoom) return undefined
    const fresh = liveAvailability.data?.find(
      (r) => r.room_type_id === initialRoom.room_type_id,
    )
    if (!fresh) return initialRoom
    return {
      ...initialRoom,
      ...fresh,
      // Preserve the richer media/copy from the original snapshot when the
      // live API doesn't return them.
      images: fresh.images && fresh.images.length > 0 ? fresh.images : initialRoom.images,
      short_description: fresh.short_description || initialRoom.short_description,
      marketing_description: fresh.marketing_description || initialRoom.marketing_description,
      amenities:
        Array.isArray(fresh.amenities) && fresh.amenities.length > 0
          ? fresh.amenities
          : initialRoom.amenities,
    }
  }, [liveAvailability.data, initialRoom])
  const isStillAvailable =
    !liveAvailability.isPending &&
    liveAvailability.data != null &&
    room != null &&
    (room.available_rooms_count ?? 0) > 0
  const [form, setForm] = useState<BookingForm>({
    first_name: customer?.first_name || '',
    last_name: customer?.last_name || '',
    email: customer?.email || '',
    phone: customer?.phone || '',
    special_requests: '',
  })
  const [authTab, setAuthTab] = useState<'login' | 'register'>('login')
  const [authForm, setAuthForm] = useState({
    first_name: customer?.first_name || '',
    last_name: customer?.last_name || '',
    email: customer?.email || '',
    phone: customer?.phone || '',
    password: '',
  })

  useEffect(() => {
    void executeRecaptcha('reserve_room_warmup')
  }, [])

  useEffect(() => {
    if (customer) {
      setForm((prev) => ({
        ...prev,
        first_name: customer.first_name,
        last_name: customer.last_name,
        email: customer.email,
        phone: customer.phone,
      }))
    }
  }, [customer])

  const loginMutation = useCustomerLogin()
  const registerMutation = useCustomerRegister()
  const reservationMutation = useCreateReservation()

  const handleLogin = () => {
    loginMutation.mutate(
      { email: authForm.email, password: authForm.password },
      {
        onSuccess: (data) => {
          setCustomerAuth(data)
          setForm((prev) => ({ ...prev, ...data.customer }))
        },
      },
    )
  }

  const handleRegister = () => {
    registerMutation.mutate(authForm, {
      onSuccess: (data) => {
        setCustomerAuth(data)
        setForm((prev) => ({ ...prev, ...data.customer }))
      },
    })
  }

  const handleReserve = () => {
    if (!room || !search) return
    // Defense in depth: don't submit if the live availability check says the
    // selected dates/party are no longer available.
    if (liveAvailability.isPending || !isStillAvailable) return
    reservationMutation.mutate(
      {
        room_type_id: room.room_type_id,
        check_in_date: search.check_in,
        check_out_date: search.check_out,
        adults: search.adults,
        children: search.children,
        account_mode: customer ? 'account' : 'guest',
        special_requests: form.special_requests,
        guest: {
          first_name: form.first_name,
          last_name: form.last_name,
          email: form.email,
          phone: form.phone,
        },
      },
      {
        onSuccess: (data) =>
          navigate('/confirmation', { state: { reservation: data } }),
      },
    )
  }

  const isValid =
    Boolean(form.first_name.trim()) &&
    Boolean(form.last_name.trim()) &&
    /\S+@\S+\.\S+/.test(form.email) &&
    form.phone.trim().length >= 7

  if (!room || !search) return <Navigate to="/" replace />

  return (
    <section className="bg-cream-light pt-24 pb-12 md:pt-32 md:pb-20">
      <div className="mx-auto grid max-w-7xl gap-6 px-4 sm:gap-8 lg:grid-cols-[2fr_1fr]">
        <div className="order-2 lg:order-1">
          <p className="mb-2 text-xs font-semibold uppercase tracking-widest text-terracotta sm:text-sm">
            Reserva directa
          </p>
          <h1 className="mb-2 font-heading text-2xl font-bold text-wood-dark sm:text-3xl md:text-4xl">
            Completa tu reserva de {room.name}
          </h1>
          <p className="mb-6 text-sm text-stone sm:mb-8 sm:text-base">
            Correo y teléfono son obligatorios. Puedes reservar como invitado o con cuenta
            de cliente.
          </p>

          <Card>
            <CardContent className="p-4 sm:p-6 md:p-8">
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <label className={fieldLabel}>Nombre</label>
                  <Input
                    value={form.first_name}
                    onChange={(e) =>
                      setForm((prev) => ({ ...prev, first_name: e.target.value }))
                    }
                  />
                </div>
                <div>
                  <label className={fieldLabel}>Apellido</label>
                  <Input
                    value={form.last_name}
                    onChange={(e) =>
                      setForm((prev) => ({ ...prev, last_name: e.target.value }))
                    }
                  />
                </div>
                <div>
                  <label className={fieldLabel}>Correo electrónico</label>
                  <Input
                    type="email"
                    value={form.email}
                    onChange={(e) =>
                      setForm((prev) => ({ ...prev, email: e.target.value }))
                    }
                  />
                </div>
                <div>
                  <label className={fieldLabel}>Teléfono</label>
                  <PhoneInput
                    value={form.phone}
                    onChange={(next) =>
                      setForm((prev) => ({ ...prev, phone: next }))
                    }
                  />
                </div>
                <div className="sm:col-span-2">
                  <label className={fieldLabel}>Solicitudes especiales</label>
                  <Textarea
                    rows={4}
                    value={form.special_requests}
                    onChange={(e) =>
                      setForm((prev) => ({ ...prev, special_requests: e.target.value }))
                    }
                  />
                </div>
              </div>

              <Button
                size="lg"
                className="mt-6 w-full"
                disabled={
                  !isValid ||
                  reservationMutation.isPending ||
                  liveAvailability.isPending ||
                  !isStillAvailable
                }
                onClick={handleReserve}
              >
                {reservationMutation.isPending
                  ? 'Creando reserva...'
                  : liveAvailability.isPending
                    ? 'Verificando disponibilidad…'
                    : !isStillAvailable
                      ? 'Sin disponibilidad'
                      : 'Crear reservación pendiente'}
              </Button>
              {reservationMutation.isError && (
                <p className="mt-3 rounded-md bg-red-50 p-3 text-sm text-red-700">
                  No se pudo completar la reserva. Revisa los datos e inténtalo de nuevo.
                </p>
              )}
            </CardContent>
          </Card>
        </div>

        <aside className="order-1 space-y-4 sm:space-y-6 lg:order-2">
          <Card>
            <CardContent className="p-4 sm:p-6">
              <img
                src={getPrimaryImage(room.images, roomImageFallback(room.name))}
                alt={room.name}
                className="mb-4 aspect-[16/9] w-full rounded-md object-cover sm:aspect-[4/3]"
              />
              <h3 className="mb-1 font-heading text-xl font-bold text-wood-dark">
                {room.name}
              </h3>
              <p className="mb-4 text-sm text-stone">{room.short_description}</p>
              <BookingDateGuestPicker
                value={search}
                onChange={setSearch}
                maxOccupancy={room.max_occupancy}
                layout="grid"
              />
              <SummaryRow
                label="Noches"
                value={String(calculateNights(search.check_in, search.check_out))}
              />
              {liveAvailability.isPending && (
                <p className="mt-2 text-xs text-stone">Recalculando precio…</p>
              )}
              {!liveAvailability.isPending && !isStillAvailable && (
                <p className="mt-2 text-xs text-terracotta">
                  No hay disponibilidad para esta selección. Cambia las fechas o el número de
                  huéspedes.
                </p>
              )}
              {room.extra_person && room.extra_person.count > 0 && (
                <div className="mt-3 flex items-baseline justify-between border-t border-cream-dark pt-3 text-sm text-stone">
                  <span>
                    Persona adicional ({room.extra_person.count} ×{' '}
                    {room.extra_person.nights} {room.extra_person.nights === 1 ? 'noche' : 'noches'})
                  </span>
                  <span>{formatCurrency(room.extra_person.amount_with_tax)}</span>
                </div>
              )}
              <div className="mt-3 flex items-baseline justify-between border-t border-cream-dark pt-3">
                <span className="text-sm text-stone">Total estimado</span>
                <strong className="text-xl font-bold text-terracotta">
                  {formatCurrency(room.estimated_total)}
                </strong>
              </div>
              {room.tax_breakdown && room.tax_breakdown.length > 0 && (
                <ul className="mt-1 space-y-0.5 text-xs text-stone">
                  {room.tax_breakdown.map((tax) => (
                    <li key={tax.tax_id} className="flex justify-between">
                      <span>{tax.name} ({tax.rate}%)</span>
                      <span>{formatCurrency(tax.amount)}</span>
                    </li>
                  ))}
                </ul>
              )}
              <p className="mt-2 text-[11px] uppercase tracking-wide text-gold">
                Impuestos incluidos (ISV + Tasa Turística)
              </p>
            </CardContent>
          </Card>

          {!customer ? (
            <Card>
              <CardContent className="p-4 sm:p-6">
                <div className="mb-4 flex rounded-md border border-cream-dark p-1 text-sm">
                  <button
                    className={`flex-1 rounded px-3 py-2 font-medium transition-colors ${authTab === 'login' ? 'bg-terracotta text-white' : 'text-wood'}`}
                    onClick={() => setAuthTab('login')}
                  >
                    Ingresar
                  </button>
                  <button
                    className={`flex-1 rounded px-3 py-2 font-medium transition-colors ${authTab === 'register' ? 'bg-terracotta text-white' : 'text-wood'}`}
                    onClick={() => setAuthTab('register')}
                  >
                    Crear cuenta
                  </button>
                </div>

                {authTab === 'register' ? (
                  <div className="grid gap-3 sm:grid-cols-2">
                    <AuthField
                      label="Nombre"
                      value={authForm.first_name}
                      onChange={(v) => setAuthForm({ ...authForm, first_name: v })}
                    />
                    <AuthField
                      label="Apellido"
                      value={authForm.last_name}
                      onChange={(v) => setAuthForm({ ...authForm, last_name: v })}
                    />
                    <AuthField
                      label="Correo"
                      type="email"
                      value={authForm.email}
                      onChange={(v) => setAuthForm({ ...authForm, email: v })}
                    />
                    <label className="block">
                      <span className={fieldLabel}>Teléfono</span>
                      <PhoneInput
                        value={authForm.phone}
                        onChange={(v) => setAuthForm({ ...authForm, phone: v })}
                      />
                    </label>
                    <div className="sm:col-span-2">
                      <AuthField
                        label="Contraseña"
                        type="password"
                        value={authForm.password}
                        onChange={(v) => setAuthForm({ ...authForm, password: v })}
                      />
                    </div>
                    <Button
                      variant="secondary"
                      className="mt-2 sm:col-span-2"
                      onClick={handleRegister}
                      disabled={registerMutation.isPending}
                    >
                      Crear cuenta y continuar
                    </Button>
                  </div>
                ) : (
                  <div className="grid gap-3">
                    <AuthField
                      label="Correo"
                      type="email"
                      value={authForm.email}
                      onChange={(v) => setAuthForm({ ...authForm, email: v })}
                    />
                    <AuthField
                      label="Contraseña"
                      type="password"
                      value={authForm.password}
                      onChange={(v) => setAuthForm({ ...authForm, password: v })}
                    />
                    <Button
                      variant="secondary"
                      onClick={handleLogin}
                      disabled={loginMutation.isPending}
                    >
                      Ingresar y usar mis datos
                    </Button>
                  </div>
                )}
                {(loginMutation.isError || registerMutation.isError) && (
                  <p className="mt-3 rounded-md bg-red-50 p-3 text-sm text-red-700">
                    No se pudo autenticar la cuenta de cliente.
                  </p>
                )}
              </CardContent>
            </Card>
          ) : (
            <Card className="bg-terracotta text-white">
              <CardContent className="flex items-start gap-3 p-4 sm:p-6">
                <CheckCircle2 className="mt-1 h-5 w-5 shrink-0" />
                <div>
                  <strong className="block">Reservando como {customer.first_name}</strong>
                  <p className="mt-1 text-sm text-white/90">
                    Tu reserva quedará asociada a tu cuenta para verla luego en “Mi Cuenta”.
                  </p>
                </div>
              </CardContent>
            </Card>
          )}
        </aside>
      </div>
    </section>
  )
}

function SummaryRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-baseline justify-between py-1.5 text-sm">
      <span className="text-stone">{label}</span>
      <strong className="text-wood-dark">{value}</strong>
    </div>
  )
}

function AuthField({
  label,
  value,
  onChange,
  type,
}: {
  label: string
  value: string
  onChange: (v: string) => void
  type?: string
}) {
  return (
    <label className="block">
      <span className={fieldLabel}>{label}</span>
      <Input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
    </label>
  )
}
